/*
 * Builds a location CollectionPage graph from the same bounded cards shown to visitors.
 * Only canonical, indexable city routes publish the graph.
 * Missing addresses use MLS labels; optional labels never suppress valid property URLs.
 */
import shared from '../../structured-data/shared.cjs';
import { getLocationPage } from '../location-path';
import { LocationSeo } from '../location-seo';

const { SITE, pageUrl, text } = shared;

export function locationGraph(pathname: string, seo: LocationSeo, cards: { name?: string | null; mlsId?: string | number | null; url: string }[]) {
  const page = getLocationPage(pathname);
  if (!page || page.kind !== 'properties' || !page.city || seo.robots !== 'index, follow'
    || !seo.canonical || !seo.title || !cards?.length) { return undefined; }
  const canonical = pageUrl(new URL(seo.canonical).pathname);
  if (canonical !== seo.canonical) { return undefined; }
  const items = cards.map(({ name, mlsId, url }) => {
    const mls = text(mlsId);
    const label = text(name) || (mls ? `MLS® ${mls}` : undefined);
    return { '@type': 'ListItem', ...(label ? { name: label } : {}), url: pageUrl(url) };
  }).filter(item => item.url).map((item, index) => ({ ...item, position: index + 1 }));
  if (!items.length) { return undefined; }
  const city = page.city.split('-').map(word => word[0].toUpperCase() + word.slice(1)).join(' ');
  const province = page.province.toUpperCase();
  const french = page.language === 'fr';
  const section = french ? ['/fr/proprietes/', 'Propriétés'] : ['/properties/', 'Properties'];
  const breadcrumbId = `${canonical}#breadcrumb`;
  const listId = `${canonical}#itemlist`;
  return { '@context': 'https://schema.org', '@graph': [
    { '@type': 'CollectionPage', '@id': `${canonical}#webpage`, url: canonical,
      name: seo.title, description: seo.description,
      about: { '@type': 'City', name: city, containedInPlace: { '@type': 'AdministrativeArea', name: province } },
      mainEntity: { '@id': listId }, breadcrumb: { '@id': breadcrumbId } },
    { '@type': 'ItemList', '@id': listId, numberOfItems: items.length, itemListElement: items },
    { '@type': 'BreadcrumbList', '@id': breadcrumbId, itemListElement: [
      { '@type': 'ListItem', position: 1, name: french ? 'Accueil' : 'Home', item: `${SITE}${french ? '/fr/' : '/'}` },
      { '@type': 'ListItem', position: 2, name: section[1], item: pageUrl(section[0]) },
      { '@type': 'ListItem', position: 3, name: city, item: canonical }
    ] }
  ] };
}
